import { ROUTER_PATHS, getSolicitudBeneficioLabel, getUrlParamsFetch, handleAxiosError } from '@/shared';
import { erpApi } from '@/shared/axios/erp-api';
import { urlBackend } from '@/shared/constants/networking.constants';
import { AlertSeverityEnum, UseMutationsParams } from '@/shared/interfaces';
import { IDataProspecto, IDataProspectoParams, IFromProspecto, IProspecto } from '@/shared/interfaces/erp/prospecto';
import { useUiStore } from '@/store/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createSolicitudTutorDescuento } from './compras';
import { useSystemParamsStore } from '@/store/erp';

const { get, post, put } = erpApi();

export type CreateProspectoProps = Omit<IDataProspecto, 'id_prospecto'>;

export const useFetchProspecto = (params: IDataProspectoParams, enabled: boolean = true) => {
  return useQuery({
    queryKey: ['prospecto', ...Object.values(params || {})],
    queryFn: async () => {
      return await getProspecto(params);
    },
    retry: false,
    enabled,
    refetchOnWindowFocus: false,
  });
};

export const getProspecto = async (params?: IDataProspectoParams) => {
  const queryParams = getUrlParamsFetch(params || {});
  const resp = get<IProspecto>(
    `${urlBackend}api/v1/prospecto/?${queryParams}`,
    true
  );
  return resp;
};

export const useGetProspectoByIdProspecto = (id?: string) => {
  return useQuery({
    queryKey: ['prospecto', id],
    queryFn: async () => {
      return getProspectoByIdProspecto(id!);
    },
    refetchOnWindowFocus: false,
    enabled: !!id,
  });
};

export const getProspectoByIdProspecto = async (id: string) => {
  const setAlert = useUiStore.getState().setAlert;
  const res = await get<IDataProspecto>(`${urlBackend}api/v1/prospecto/${id}/`, true);
  res.status === 'error' && setAlert(true, res.message, 'error');

  return res.data;
};

export const useCreateProspecto = ({
  navigate,
  returnUrl = ROUTER_PATHS.ventas.preventasCrearNav
}: UseMutationsParams) => {
  const queryClient = useQueryClient();

  const setAlert = useUiStore.getState().setAlert;
  const setConfirmDialog = useUiStore.getState().setConfirmDialog;
  const setConfirmDialogIsOpen = useUiStore.getState().setConfirmDialogIsOpen;
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  const valorMinimoEquifax = useSystemParamsStore.getState().valorMinEquifax


  return useMutation({

    mutationFn: createProspecto,
    onSuccess: async (data, variables ) => {
      queryClient.invalidateQueries({ queryKey: ['prospecto'] });
      if (data.code == 200) {
        setConfirmDialogIsOpen(true);

        const prospecto = data.data;

        if (prospecto.es_curador_tutor
          || prospecto.es_bono_humano
          || prospecto.es_ayuda_social
          || prospecto.es_reportado
          || prospecto.reportado
          || prospecto.es_gremio
        ) {
          setIsGlobalLoading(true)

          const res = await createSolicitudIngreso({
            prospecto: prospecto.codigo,
            es_bono_humano: prospecto.es_bono_humano,
            es_tutor: prospecto.es_curador_tutor,
            es_ayuda_social: prospecto.es_ayuda_social,
            es_reportado: prospecto.es_reportado || prospecto.reportado,
            observaciones: {
              observacion_solicitud: variables.observacion_solicitud,
              observacion_score_bajo: +prospecto.puntaje_score_empresa < valorMinimoEquifax?.score_empresa!
                ? 'Cliente presenta un puntaje Equifax inferior al mínimo requerido.'
                : null,
              observacion_reportado: prospecto.reportado
                ? 'Cliente presenta antecedentes de problemas con otras empresas de telecomunicaciones.'
                : null
            }
          })

          if (res?.code == 200) {

            setIsGlobalLoading(false)

            setConfirmDialog({
              isOpen: true,
              title: '¡Solicitud creado con éxito!',
              subtitle:
                `La solicitud de beneficio "${getSolicitudBeneficioLabel({
                  es_bono_humano: prospecto.es_bono_humano,
                  es_gremio: prospecto.es_gremio,
                  es_reportado: prospecto.es_reportado,
                  reportado:  prospecto.reportado,
                  es_tutor: prospecto.es_curador_tutor,
                  es_ayuda_social: prospecto.es_ayuda_social
                })}" se creó correctamente. En breve recibirás una respuesta o, si lo prefieres, puedes comunicarte con tu supervisor.`,
              onConfirm() {
                setConfirmDialogIsOpen(false);
                navigate && navigate(ROUTER_PATHS.ventas.prospectoNav);
              },
              cancelTextBtn: 'Cerrar',
            });

          } else {
            setAlert(true, "Ha ocurrido un error al generar la solicitud. Intente nuevamente", 'error')
          }
          return
        }


        setConfirmDialog({
          isOpen: true,
          title: '¡Prospecto creado con éxito!',
          subtitle:
            'El prospecto se ha creado exitosamente. El siguiente paso es proceder con la creación de una preventa. ¿Deseas continuar?',
          onClose() {
            window.location.reload();
          },
          onConfirm() {
            setConfirmDialogIsOpen(false);
            navigate && navigate(`${returnUrl}/${prospecto.id_prospecto}`);
          },
          cancelTextBtn: 'Cerrar',
        });
        return;
      }

      setAlert(true, data.message, data.status);
    },

    onError: (e => {
      handleAxiosError(e);
    }),
    onSettled: () => {
      setIsGlobalLoading(false);
    }
  });
};

export const useFetchCreateProspecto = ({
}: UseMutationsParams) => {
  const queryClient = useQueryClient();


  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;


  return useMutation({

    mutationFn: createProspecto,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['prospecto'] });
    },

    onError: (e => {
      setIsGlobalLoading(false);
      handleAxiosError(e);
    }),
  });
};

export const createProspecto = async (data: IFromProspecto) => {
  const res = await post<IDataProspecto>(`${urlBackend}api/v1/prospecto/`, data, true);
  return res;
};

const createSolicitudIngreso = async (data: any) => {
  const res = await createSolicitudTutorDescuento(data)
  return res
}


export const useFetchUpdateProspecto = ({
  enableAlert = false
}: UseMutationsParams) => {

  const setAlert = useUiStore.getState().setAlert;

  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id: string;
      data: IDataProspectoParams;
    }) => {
      return updateProspecto({ data: data, id: id });
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['prospecto'] });

      enableAlert && setAlert(true, data.message, AlertSeverityEnum.success)
    },
    onError: (error) => {
      handleAxiosError(error);
    }
  });
};

export const updateProspecto = async (
  { id, data }: {
    id: string,
    data: IDataProspectoParams
  }
) => {
  const res = await put<IDataProspecto>(`${urlBackend}api/v1/prospecto/${id}/`, data, true);
  return res;
};
